import { useCallback } from 'react';
import { Button, ModeEditTwoToneIcon } from 'tuui';
import { HelpTooltip, INDICATION_TYPES, IndicationContent, TextCheckbox } from 'taboola-ultimate-ui';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { ConversionCategoryField } from 'modules/tracking/components/ConversionCategoryField/ConversionCategoryField';
import { TotalValueTooltip } from 'modules/tracking/components/TotalValueCheckBox/TotalValueTooltip/TotalValueTooltip';
import { CODELESS_CATEGORIES } from 'modules/tracking/consts/CodelessCategory';
import { useCurrentConversion } from '../../hooks/useCurrentConversion';
import { useDeleteConversionEvent } from '../../hooks/useDeleteConversionEvent';
import { useUpdateConversion } from '../../hooks/useUpdateConversion';
import { NewEventHeader } from '../Headers/NewEventHeader';
import { ConversionNameField } from './ConversionNameField';
import styles from './setupClicksEventCategory.module.scss';

export const SetupClicksEventCategory = ({ goNext, goBack, resetSteps, currentIndex }) => {
    const currentConversion = useCurrentConversion(currentIndex);

    const buttonName = currentConversion?.eventName;
    const conversionName = currentConversion?.conversionName;
    const handleConversionNameChange = useUpdateConversion({ fieldName: 'conversionName' });
    const handleConversionName = useCallback(
        value => handleConversionNameChange({ currentIndex, value }),
        [handleConversionNameChange, currentIndex]
    );

    const includeInTotalConversions = currentConversion?.includeInTotalConversions;
    const handleIncludeInTotalChange = useUpdateConversion({
        fieldName: 'includeInTotalConversions',
    });
    const handleSelectCheckbox = () => handleIncludeInTotalChange({ currentIndex, value: true });
    const handleUnselectCheckbox = () => handleIncludeInTotalChange({ currentIndex, value: false });
    const handleDeleteConversion = useDeleteConversionEvent(currentConversion?.id, resetSteps);

    return (
        <>
            <NewEventHeader handlerArrowLeftButton={goBack} />
            <div className={styles['title-and-selector-container']}>
                <IndicationContent containerClassName={styles['container-indication']} type={INDICATION_TYPES.SUCCESS}>
                    <FormattedMessage
                        id="codeless.setup.clicks.event.category.indication"
                        defaultMessage={`<strong>‘${buttonName}</strong>' Button was selected`}
                        values={{ value: buttonName }}
                    />
                    <div className={styles['right-icon']} onClick={goBack}>
                        <ModeEditTwoToneIcon />
                    </div>
                </IndicationContent>

                <div className={styles['setup-title']}>
                    <FormattedMessage
                        id="codeless..setup.clicks.event.category.header"
                        defaultMessage="Setup button clicks event"
                    />
                </div>
                <ConversionNameField
                    conversionName={conversionName}
                    buttonName={buttonName}
                    onConversionNameChange={handleConversionName}
                />
                <ConversionCategoryField
                    isCodeless={true}
                    field={`newConversion.${currentIndex}.category`}
                    skipValidations={false}
                    categories={CODELESS_CATEGORIES}
                />
            </div>
            <div className={styles['checkbox-container']}>
                <TextCheckbox
                    labelClassName={styles['checkbox-label']}
                    onSelectCheckbox={handleSelectCheckbox}
                    onUnselectCheckbox={handleUnselectCheckbox}
                    selectedValues={[includeInTotalConversions]}
                    independent
                    value
                    title={
                        <FormattedMessage
                            id="tracking.conversion.properties.total.conversion.checkbox.title"
                            defaultMessage="Include in total conversions"
                        />
                    }
                    data-metrics-component="Total value checkbox"
                    data-metrics-event-name={GTM_EVENTS.USABILITY}
                    data-metrics-value={includeInTotalConversions}
                />
                <HelpTooltip>
                    <TotalValueTooltip />
                </HelpTooltip>
            </div>

            <footer className={styles['footer']}>
                <Button
                    onClick={handleDeleteConversion}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={goNext}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={
                        !currentConversion?.category ||
                        !currentConversion?.eventName ||
                        !currentConversion.conversionName
                    }
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </>
    );
};
